import { FunctionComponent, useEffect } from 'react';
import * as Yup from 'yup';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import { FormikErrors, useFormik } from 'formik';

// Component
import Image from '@Components/Utils/Image';
import ImgLazy from '@Components/Utils/Lazy/Image';
import { getHttpErrorMessage, http } from '@Libs/http';
import { Modal as RTModal, ModalBody } from 'reactstrap';
import { useAccountContext } from '@Components/Account/Contexts';
import { useViewDetected } from '@Components/Utils/ViewDetected';

// styles
import styles from './Modal.module.scss';

// Libs
import { phoneRegExp } from '@Libs/utils';
import serviceApi from '@Libs/api/user/service';
import { useServiceManager } from '../Contexts/service';
import { useAlertContext } from '@Components/Alert/Context';

interface ModalProps {
    className?: string;
}

const validationSchema = Yup.object().shape({
    comment: Yup.string(),
    fullName: Yup.string()
        .matches(/^(\s+\S+\s*)*(?!\s).*$/, 'Không thể nhập khoảng trắng')
        .required('Vui lòng nhập họ tên'),
    phoneNumber: Yup.string().matches(phoneRegExp, 'Số điện thoại chưa đúng').required('Vui lòng nhập số điện thoại'),
});

const Modal: FunctionComponent<ModalProps> = ({ className }) => {
    const { alert } = useAlertContext();
    const { summary } = useAccountContext();

    const selfProfile = (summary?.consumer?.profiles || []).find(({ relation }) => relation === 'self') || {};
    const phoneNumberReplace = summary?.consumer?.phoneNumber.replace('+84', '0') || '';

    useEffect(() => {
        setFieldValue('fullName', selfProfile?.fullName, !!errors['fullName']);
        setFieldValue('phoneNumber', phoneNumberReplace, !!errors['phoneNumber']);
    }, [summary]);

    const { deviceType } = useViewDetected();
    const isMobile = ['mobile', 'tablet'].includes(deviceType);
    const { isRegister, packageName, setIsRegister, setIsSuccess } = useServiceManager();

    const wrapperClassName = classNames([
        styles.wrapper,
        {
            [styles.mobile]: isMobile,
        },
        className,
    ]);

    const source = Cookies.get('source');
    const channel = Cookies.get('channel');
    const referral = Cookies.get('referral');
    const utm_term = Cookies.get('utm_term');
    const extraData = Cookies.get('extraData');
    const traffic_id = Cookies.get('traffic_id');
    const utm_medium = Cookies.get('utm_medium');
    const utm_source = Cookies.get('utm_source');
    const utm_content = Cookies.get('utm_content');
    const url_referral = Cookies.get('url_referral');
    const utm_campaign = Cookies.get('utm_campaign');
    const internal_channel = Cookies.get('internal_channel') || 'website';

    const handleSalesOrder = async () => {
        try {
            const payload = {
                consumerInfo: {
                    fullName: values.fullName,
                    phoneNumber: values.phoneNumber,
                },
                comment: `${packageName} ${values?.comment && `\n ${values?.comment}`}`,
                tracking: {
                    ...(!!source && { source }),
                    ...(!!channel && { channel }),
                    ...(!!referral && { referral }),
                    ...(!!utm_term && { utm_term }),
                    ...(!!traffic_id && { traffic_id }),
                    ...(!!extraData && { extraData }),
                    ...(!!utm_source && { utm_source }),
                    ...(!!utm_medium && { utm_medium }),
                    ...(!!utm_content && { utm_content }),
                    ...(!!url_referral && { url_referral }),
                    ...(!!utm_campaign && { utm_campaign }),
                    ...(!!internal_channel && { internal_channel }),
                },
            };
            await serviceApi(http).salesOrder(payload);

            setValues(
                {
                    comment: '',
                    fullName: selfProfile?.fullName || '',
                    phoneNumber: phoneNumberReplace,
                },
                false,
            );

            setIsRegister(!isRegister);
            setIsSuccess(true);

            gtag('event', 'click', {
                event_category: 'Services',
                event_label: 'Click - Modal Register',
            });
        } catch (error) {
            alert({
                type: 'error',
                title: 'Đã xảy ra lỗi',
                text: getHttpErrorMessage(error),
            });
        }
    };

    const { errors, values, setErrors, setFieldValue, setValues, handleSubmit } = useFormik({
        validationSchema,
        initialValues: {
            comment: '',
            fullName: selfProfile?.fullName || '',
            phoneNumber: phoneNumberReplace,
        },
        onSubmit: handleSalesOrder,
    });

    const onChangeComment = (event) => {
        setFieldValue('comment', event.target.value, !!errors['comment']);
    };

    const onChangeName = (event) => {
        setFieldValue('fullName', event.target.value, !!errors['fullName']);
    };

    const onChangePhoneNumber = (event) => {
        setFieldValue('phoneNumber', event.target.value, !!errors['phoneNumber']);
    };
    const onCloseModal = () => {
        setIsRegister(!isRegister);
        setValues(
            {
                comment: '',
                fullName: selfProfile?.fullName || '',
                phoneNumber: phoneNumberReplace,
            },
            false,
        );

        const errors: FormikErrors<{
            comment: string;
            fullName: any;
            phoneNumber: any;
        }> = {};
        setErrors(errors);
    };
    const banner = isMobile ? 'banner_modal_mobile' : 'banner_modal';

    return (
        <RTModal isOpen={isRegister} centered contentClassName={wrapperClassName}>
            <ModalBody className={styles.body}>
                <div className={styles.head}>
                    <ImgLazy className={styles.banner} src={`assets/dich-vu/${banner}.png`} />
                </div>
                <div className={styles.content}>
                    <div className={styles.icon_close} onClick={onCloseModal}>
                        <Image src={`assets/dich-vu/${isMobile ? 'icon_close_mobile' : 'icon_close'}.png`} />
                    </div>
                    <div className={styles.title}>Đăng ký</div>

                    <form onSubmit={handleSubmit} className={styles.form_register}>
                        <input
                            value={values.fullName}
                            className={styles.name_input}
                            placeholder="Nhập họ và tên"
                            onChange={(event) => onChangeName(event)}
                        />
                        {errors.fullName && <label className={styles.text_error}>{errors.fullName}</label>}
                        <div className={styles.phone_group}>
                            <div className={styles.country_phone}>
                                <Image className={styles.icon} src="assets/dich-vu/icon_flag.png" />
                                +84
                            </div>
                            <input
                                name="phoneNumber"
                                value={values.phoneNumber}
                                className={styles.phone_input}
                                placeholder="Nhập số điện thoại"
                                onChange={(event) => onChangePhoneNumber(event)}
                                type={'text'}
                            />
                        </div>
                        {errors.phoneNumber && <label className={styles.text_error}>{errors.phoneNumber}</label>}

                        <textarea
                            rows={4}
                            value={values.comment}
                            className={styles.text_area}
                            onChange={(event) => onChangeComment(event)}
                            placeholder={'Nhập nội dung bạn cần tư vấn...'}
                        />

                        <div className={styles.button}>
                            <button type="submit" className={styles.btn_button}>
                                Đăng ký ngay
                            </button>
                        </div>
                    </form>
                </div>
            </ModalBody>
        </RTModal>
    );
};
export default Modal;
